/**
 * @fileoverview Helper utilities and constants for the authentication context
 * This module provides reusable functions and constants for managing authentication
 * state, online/offline connectivity, and data validation for the AuthContext.
 *
 * Sync services are lazy-loaded only when syncConfig.enableSync is true, avoiding
 * "Cannot access X before initialization" errors from circular deps when sync is disabled.
 */
import { apiService } from '../services/apiService';
import { DB_CONSTANTS } from '../services/dbHelpers/dbStorageHelper';
import { validateId } from '../utils/validation';
import { syncConfig } from '../config';

// Import and re-export from authConstants (sync services import authConstants directly)
import { AUTH_STATES, CONNECTION_STATES, LOADING_STATES } from './authConstants';
export { AUTH_STATES, CONNECTION_STATES, LOADING_STATES };

/**
 * Lazy-load sync services only when enableSync is true. Returns null when sync is disabled.
 * Caches result to avoid repeated dynamic imports.
 * @returns {Promise<{syncCoordinator: Object|null, syncService: Object|null}>}
 */
let _syncCache = null;
export async function getSync() {
  if (!syncConfig.enableSync) {
    return { syncCoordinator: null, syncService: null };
  }
  if (_syncCache) return _syncCache;
  const [coordModule, serviceModule] = await Promise.all([
    import('../services/SyncCoordinator'),
    import('../services/syncService')
  ]);
  _syncCache = { syncCoordinator: coordModule.default, syncService: serviceModule.default };
  return _syncCache;
}

/**
 * Debounces a function call to prevent rapid repeated executions
 * Useful for handling events that might fire frequently like tab visibility or network changes
 * 
 * @param {Function} func - Function to debounce
 * @param {number} wait - Delay in milliseconds before executing the function
 * @returns {Function} Debounced function that will only execute after wait time
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Validates user data structure and IDs
 * Ensures the user object has a properly formatted UUID
 * 
 * @param {Object|null} userData - User data to validate
 * @returns {Object|null} Validated user data or null if invalid
 */
export const validateUserData = (userData) => {
  if (!userData) return null;
  try {
    // Validate UUID if present
    if (userData.id) {
      validateId(userData.id, {
        allowLegacy: false,
        paramName: 'User ID'
      });
    }
    return userData;
  } catch (error) {
    return null;
  }
};

/**
 * Checks actual server connectivity beyond browser's online status
 * Provides more accurate connection status by verifying API server availability
 * 
 * @returns {Promise<boolean>} True if server is reachable, false otherwise
 */
export const checkServerConnectivity = async () => {
  return await apiService.checkServerHealth();
};

/**
 * Initializes sync services after successful authentication
 * Resets authentication state, initializes sync coordinator, and triggers immediate sync
 * with improved error handling and resilience. No-op when sync is disabled.
 *
 * @returns {Promise<void>}
 */
export const initializeSync = async () => {
  try {
    const { syncCoordinator, syncService } = await getSync();
    if (!syncCoordinator || !syncService) return;

    syncService.resetAuthenticationState();
    await syncCoordinator.initialize({ triggerSyncAfterInit: true }).catch(error => {
    });
  } catch (error) {
  }
};

/**
 * Handles database cleanup during logout with retry mechanism
 * Attempts to clean up database and falls back to localStorage cleanup if necessary
 * 
 * @param {Object} dbStorageService - Database storage service instance
 * @returns {Promise<void>}
 */
export const performDatabaseCleanup = async (dbStorageService) => {
  try {
    await dbStorageService.cleanup();
  } catch (dbError) {

    // Wait a short time and try once more
    try {
      // Small delay before retry
      await new Promise(resolve => setTimeout(resolve, 800));
      await dbStorageService.cleanup();
    } catch (retryError) {
      // Continue with logout even if database cleanup fails

      // As a last resort, try deleting all localStorage related to the database
      try {
        for (const key in localStorage) {
          if (key.startsWith('db') || key.includes('database') || key === DB_CONSTANTS.DB_VERSION_KEY || key === DB_CONSTANTS.DYNAMIC_TABLES_KEY) {
            localStorage.removeItem(key);
          }
        }
      } catch (e) {
      }
    }
  }
};

/**
 * Handles auth error flows including token refresh attempts and connection state management
 * Used to extract complex logic from the handleAuthError event handler
 * 
 * @param {Object} params - Parameters
 * @param {Function} params.setLoading - Function to set loading state
 * @param {Function} params.setConnectionState - Function to set connection state
 * @param {string} params.authState - Current auth state
 * @param {Function} params.tokenService - Token service instance
 * @param {Function} params.userStorage - User storage instance
 * @param {Function} params.updateUser - Function to update user data
 * @param {Function} params.setAuthState - Function to set auth state
 * @param {Function} params.syncCoordinator - SyncCoordinator instance
 * @param {Function} params.dbStorageService - Database storage service instance
 * @returns {Promise<boolean>} - True if handled successfully, false if cleanup was needed
 */
export const handleAuthErrorFlow = async ({
  setLoading,
  setConnectionState,
  authState,
  tokenService,
  userStorage,
  updateUser,
  setAuthState,
  syncCoordinator,
  dbStorageService
}) => {
  try {
    // Check if we have a refresh token and we're online
    if (await tokenService.hasRefreshToken()) {
      try {
        setLoading(true); // Set loading while refreshing token

        // If we're offline, set offline state immediately
        if (!navigator.onLine) {
          setConnectionState(CONNECTION_STATES.OFFLINE);
          // If we were previously authenticated, maintain that state
          if (authState === AUTH_STATES.AUTHENTICATED) {
            setLoading(false);
            return true;
          }
        }

        const refreshData = await tokenService.refreshAccessToken();

        // Handle offline response from tokenService
        if (refreshData?.offline) {
          setConnectionState(CONNECTION_STATES.OFFLINE);
          // If we were previously authenticated, maintain that state
          if (authState === AUTH_STATES.AUTHENTICATED) {
            setLoading(false);
            return true;
          }
        }

        // Only handle user data and auth state since tokens are already stored in tokenService
        if (refreshData?.user) {

          // Validate and store user data
          const validatedUser = validateUserData(refreshData.user);
          if (!validatedUser) {
            throw new Error('Invalid user data in refresh response');
          }

          // Update user data in storage and state
          await userStorage.setUserData(validatedUser);
          await updateUser(validatedUser);

          // Set auth state
          localStorage.setItem('auth_state', AUTH_STATES.AUTHENTICATED);
          setAuthState(AUTH_STATES.AUTHENTICATED);

          setLoading(false);
          return true; // Exit early on successful refresh
        } else {
          throw new Error('No user data in refresh response');
        }
      } catch (refreshError) {
        // Handle network errors by setting offline state
        if (!navigator.onLine || refreshError.message?.includes('Network Error') || refreshError.isOffline) {
          setConnectionState(CONNECTION_STATES.OFFLINE);
          setLoading(false);
          return true;
        }
        // Only proceed with logout if refresh fails and it's not a network error
      } finally {
        setLoading(false);
      }
    } else if (!navigator.onLine) {
      // If we don't have a refresh token but we're offline, set offline state
      setConnectionState(CONNECTION_STATES.OFFLINE);
      setAuthState(AUTH_STATES.UNAUTHENTICATED);
      localStorage.setItem('auth_state', AUTH_STATES.UNAUTHENTICATED);
      return true;
    }

    // Only proceed with cleanup if we couldn't refresh the token or if refresh failed
    if (syncCoordinator) await syncCoordinator.destroy();
    await dbStorageService.cleanup();
    tokenService.deleteAccessToken();
    tokenService.deleteRefreshTokenAndKey();
    //localStorage.clear();
    setAuthState(AUTH_STATES.UNAUTHENTICATED);
    await updateUser(null);
    return false;
  } catch (error) {
    setAuthState(AUTH_STATES.UNAUTHENTICATED);
    await updateUser(null);
    return false;
  }
};

/**
 * Handles offline authenticated state
 * When user is offline but already authenticated, maintains the authenticated state
 * 
 * @param {Object} params - Parameters
 * @param {Function} params.setConnectionState - Function to set connection state
 * @param {Function} params.setLoading - Function to set loading state
 * @param {Function} params.setLoadingState - Function to set loading state
 * @returns {boolean} - Whether offline authenticated state was handled
 */
export const handleOfflineAuthenticatedState = ({
  setConnectionState,
  setLoading,
  setLoadingState
}) => {
  if (!navigator.onLine) {
    setConnectionState(CONNECTION_STATES.OFFLINE);
    setLoading(false);
    setLoadingState(LOADING_STATES.IDLE);
    return true;
  }
  return false;
};

/**
 * Handles token refresh during authentication check
 * Attempts to refresh the token and updates the state accordingly
 * 
 * @param {Object} params - Parameters
 * @param {string} params.authState - Current auth state
 * @param {Function} params.setTokenRefreshInProgress - Function to set token refresh progress state
 * @param {Function} params.tokenService - Token service instance
 * @param {Function} params.setConnectionState - Function to set connection state
 * @param {Function} params.setLoading - Function to set loading state
 * @param {Function} params.setLoadingState - Function to set loading state
 * @returns {Promise<Object>} - Result of the token refresh attempt
 * @throws {Error} - If token refresh fails
 */
export const handleTokenRefresh = async ({
  authState,
  setTokenRefreshInProgress,
  tokenService,
  setConnectionState,
  setLoading,
  setLoadingState
}) => {
  // Check if we have a refresh token
  if (await tokenService.hasRefreshToken()) {
    try {
      // Handle offline state if applicable
      if (!navigator.onLine) {
        setConnectionState(CONNECTION_STATES.OFFLINE);
        if (authState === AUTH_STATES.AUTHENTICATED) {
          setLoading(false);
          setLoadingState(LOADING_STATES.IDLE);
          return { offline: true };
        }
      }

      // Set token refresh in progress
      setTokenRefreshInProgress(true);

      // Attempt to refresh the token
      const refreshResult = await tokenService.refreshAccessToken();

      // Set token refresh as complete
      setTokenRefreshInProgress(false);

      // Handle offline response
      if (refreshResult?.offline) {
        setConnectionState(CONNECTION_STATES.OFFLINE);
        if (authState === AUTH_STATES.AUTHENTICATED) {
          setLoading(false);
          setLoadingState(LOADING_STATES.IDLE);
        }
      }

      return refreshResult;
    } catch (refreshError) {
      // Set token refresh as complete
      setTokenRefreshInProgress(false);

      // Handle network errors
      if (!navigator.onLine || refreshError.message?.includes('Network Error') || refreshError.isOffline) {
        setConnectionState(CONNECTION_STATES.OFFLINE);
        return { offline: true };
      }

      throw new Error('Authentication failed - unable to refresh token');
    }
  } else {
    throw new Error('Authentication failed - no refresh token available');
  }
};

/**
 * Sets up user data and sync after successful authentication
 * Updates user data and initializes sync if online
 * 
 * @param {Object} params - Parameters
 * @param {Object} params.validatedData - Validated user data
 * @param {Function} params.updateUser - Function to update user data
 * @param {Function} params.setConnectionState - Function to set connection state
 * @param {Object} params.syncCoordinator - SyncCoordinator instance
 * @returns {Promise<void>}
 */
export const setupUserAndSync = async ({
  validatedData,
  updateUser,
  setConnectionState,
  syncCoordinator
}) => {
  if (!navigator.onLine) {
    await updateUser(validatedData);
    setConnectionState(CONNECTION_STATES.OFFLINE);
  } else {
    await updateUser(validatedData);
    setConnectionState(CONNECTION_STATES.ONLINE);
    if (syncCoordinator) {
      await syncCoordinator.initialize();
      await syncCoordinator.triggerSync();
    }
  }
};

/**
 * Handles auth check errors
 * Determines whether to set offline state or clean up auth state
 * 
 * @param {Object} params - Parameters
 * @param {Error} params.error - The error that occurred
 * @param {string} params.authState - Current auth state
 * @param {Function} params.userStorage - User storage instance
 * @param {Function} params.setConnectionState - Function to set connection state
 * @param {Function} params.updateUser - Function to update user data
 * @param {Function} params.setAuthState - Function to set auth state
 * @param {Object} params.syncCoordinator - SyncCoordinator instance
 * @returns {Promise<void>}
 */
export const handleAuthCheckError = async ({
  error,
  authState,
  userStorage,
  setConnectionState,
  updateUser,
  setAuthState,
  syncCoordinator
}) => {
  const isNetworkError = !navigator.onLine ||
    error.message?.includes('Network Error') ||
    error.isOffline;

  const wasAuthenticated = authState === AUTH_STATES.AUTHENTICATED ||
    authState === AUTH_STATES.UNAUTHENTICATED;

  // Try to get user data
  const userData = await userStorage.getUserData();

  // Only set to offline if previously authenticated and it's a network error
  if (isNetworkError && wasAuthenticated && userData) {
    setConnectionState(CONNECTION_STATES.OFFLINE);
  } else {
    await updateUser(null);
    setAuthState(AUTH_STATES.UNAUTHENTICATED);
    if (syncCoordinator) await syncCoordinator.destroy();
  }
};

/**
 * Executes the logout procedure with proper cleanup
 * Handles updating auth state, calling logout API (if online), and cleaning up resources
 * 
 * @param {Object} params - Parameters
 * @param {Function} params.setAuthState - Function to set auth state
 * @param {Function} params.updateUser - Function to update user data
 * @param {string} params.connectionState - Current connection state
 * @param {Object} params.apiService - API service instance
 * @param {Object} params.syncCoordinator - SyncCoordinator instance
 * @param {Object} params.syncService - Sync service instance
 * @param {Object} params.tokenService - Token service instance
 * @param {Object} params.dbStorageService - Database storage service instance
 * @returns {Promise<void>}
 */
export const executeLogout = async ({
  setAuthState,
  updateUser,
  connectionState,
  apiService,
  syncCoordinator,
  syncService,
  tokenService,
  dbStorageService
}) => {
  // Generate a logout ID to track this specific logout operation
  const logoutId = Date.now();
  window._currentLogoutId = logoutId;

  // Set a guard flag so that if the page reloads mid-logout (e.g. window.location.href = '/'),
  // validateInitialState() will not attempt to restore a stale session from IndexedDB.
  localStorage.setItem('logout_in_progress', 'true');

  // Clear localStorage and tokens first, synchronously, before any await.
  // This ensures a remount (e.g. after redirect) always sees unauthenticated state
  // even if we throw or return early later.
  localStorage.setItem('auth_state', AUTH_STATES.UNAUTHENTICATED);
  try {
    tokenService.deleteAccessToken();
    tokenService.deleteRefreshTokenAndKey();
    // Clear "login as user" keys so remount does not restore that session
    localStorage.removeItem('login_as_user_token');
    localStorage.removeItem('login_as_user_expires_at');
    localStorage.removeItem('login_as_user_active');
    localStorage.removeItem('login_as_user_name');
  } catch (storageErr) {
  }

  try {
    // Update React state and user so UI reflects logout immediately
    setAuthState(AUTH_STATES.UNAUTHENTICATED);
    await updateUser(null);

    // Always attempt logout API call regardless of connectionState.
    // The try/catch inside handles truly offline cases.
    try {
      // Get clientKey to send to backend for proper token revocation
      const tokenData = await tokenService.getRefreshTokenAndKey();
      const clientKey = tokenData?.clientKey || null;
      await apiService.logout(clientKey);
    } catch (err) {
    }

    // Check if user has already logged back in (new login started)
    if (window._currentLogoutId !== logoutId) {
      return;
    }

    // Emit event to let all services know we're logging out
    window.dispatchEvent(new CustomEvent('user-logout-initiated'));

    if (syncCoordinator) {
      try {
        await syncCoordinator.destroy();
      } catch (syncError) {
      }
    }

    if (window._currentLogoutId !== logoutId) {
      return;
    }

    if (syncService) syncService.clearLastSyncTime();

    // Clear stored tokens and update auth state. Use finally to ensure 
    // auth_state is cleared even if storage operations fail.
    try {
      tokenService.deleteAccessToken();
      tokenService.deleteRefreshTokenAndKey(); // clears localStorage user data
    } finally {
      setAuthState(AUTH_STATES.UNAUTHENTICATED);
      localStorage.setItem('auth_state', AUTH_STATES.UNAUTHENTICATED);
    }

    // Add a small delay to allow services to terminate connections
    await new Promise(resolve => setTimeout(resolve, 200));

    // Final check before database cleanup (the slow operation)
    if (window._currentLogoutId !== logoutId) {
      return;
    }

    // Clean up database
    await performDatabaseCleanup(dbStorageService);
  } catch (err) {
    localStorage.setItem('auth_state', AUTH_STATES.UNAUTHENTICATED);
    // State is already set to unauthenticated at the start of the function
  } finally {
    // Clear the guard flag now that cleanup is complete.
    // If the page already reloaded before we got here, this is a no-op
    // (the remounted validateInitialState already consumed the flag).
    localStorage.removeItem('logout_in_progress');
  }
};